// @ts-nocheck
import { Dictionary } from "@insite/client-framework/Common/Types";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import HeaderLink, { HeaderLinkStyles } from "@store/Library/Components/Toolkit/HeaderLink";
import CoolCalc, { CoolCalcOffset } from "@store/Library/Components/Icons/CoolCalc";
import PartFinder, { PartFinderOffset } from "@store/Library/Components/Icons/PartFinder";
import RebateCalculator, { RebateCalculatorOffset } from "@store/Library/Components/Icons/RebateCalculator";
import Warranty, { WarrantyOffset } from "@store/Library/Components/Icons/Warranty";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { IconMap } from "@toolkit/Framework/Types/Icon";
import { iconColorStyles, iconScalableStyles } from "@toolkit/Themes/common/mixins/icon";
import React, { FC } from "react";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    destination = "destination",
    overrideTitle = "overrideTitle",
    icon = "icon",
}

export interface HeaderContentLinkStyles {
    headerLink?: HeaderLinkStyles;
}

export const headerContentLinkStyles: HeaderContentLinkStyles = {
    headerLink: {
        link: {
            css: css`
                display: flex;
                position: relative;
                height: 100%;
                background-color: ${getColor("menu.secondaryNavigation.linksSection")};
                border-right: 1px solid ${getColor("common.border")};

                ${IconWrapper} {
                    ${iconScalableStyles("100%")}
                    ${iconColorStyles("menu.secondaryNavigation.contrast")}
                }

                &:hover {
                    background-color: ${getColor("menu.secondaryNavigation.contrast")};
                    color: ${getColor("menu.secondaryNavigation.linksSection")};
                }

                &:last-child {
                    border-right: none;
                }

                ${breakpoint("md")} {
                    padding-right: ${pxToRem(30)};
                    padding-left: ${pxToRem(30)};
                }
            `,
            icon: {
                iconProps: {
                    size: 24,
                },
            },
        },
    },
};

export const iconMap: Dictionary<IconMap | undefined> = {
    partFinder: {
        src: PartFinder,
        offset: PartFinderOffset,
    },
    warranty: {
        src: Warranty,
        offset: WarrantyOffset,
    },
    coolCalc: {
        src: CoolCalc,
        offset: CoolCalcOffset,
    },
    rebateCalculator: {
        src: RebateCalculator,
        offset: RebateCalculatorOffset,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.destination]: LinkFieldValue;
        [fields.overrideTitle]: string;
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps;

const HeaderContentLink: FC<Props> = ({ fields, isCompactHeaderCart }) => {
    const { title, url } = useGetLink(fields.destination);
    const icon = iconMap[fields.icon || "none"];
    const showIconOnly = fields.visibilityState === "icon" || isCompactHeaderCart;

    return (
        <HeaderLink
            extendedStyles={headerContentLinkStyles.headerLink}
            href={url}
            iconSrc={icon?.src}
            showIconOnly={showIconOnly}
            label={fields.overrideTitle || title}
            data-test-selector="cartLink"
        ></HeaderLink>
    );
};

const widgetModule: WidgetModule = {
    component: withTheme(HeaderContentLink),
    definition: {
        displayName: "Content Link",
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Icon and Label",
                        value: "both",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.icon,
                displayName: "Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Part Finder",
                        value: "partFinder",
                    },
                    {
                        displayName: "Warranty",
                        value: "warranty",
                    },
                    {
                        displayName: "CoolCalc",
                        value: "coolCalc",
                    },
                    {
                        displayName: "Rebate Calculator",
                        value: "rebateCalculator",
                    },
                ],
                defaultValue: "partFinder",
                fieldType: "General",
                sortOrder: 2,
                isRequired: true,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
